import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { BootSpinner } from '@ovComponents/1-primative/bootSpinner/bootSpinner';
import {
  IdpLogout,
  isIdpLogoutRoute,
} from '@ovComponents/5-page/idpLogout/idpLogout';
import { AuthProvider } from '@providers/AuthProvider';
import { AnalyticsProvider } from '@providers/analyticsProvider';
import Auth0Provider from '@providers/auth0Provider';
import { ThemeProvider } from '@providers/themeProvider';
import { useContext, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { LocalizedDatePickerProvider } from './components/fields/localizedDatePicker';
import OvApolloProvider from './providers/ovApolloProvider';
import UserContextProvider, {
  UserContext,
} from './providers/userContextProvider';
import { AppRouter } from './routing/appRouter';
import 'quill/dist/quill.snow.css';
import 'quill-table-ui/dist/index.css';
import { CONFIG } from '@config';
import flagsmith from '@flagsmith/flagsmith';
import { FlagsmithProvider } from '@flagsmith/flagsmith/react';
import { Box, useTheme } from 'hephaestus/web';
import InactivityTimeoutProvider from './providers/inActivityTimeoutProvider';

if (CONFIG.datadog.env) {
  datadogLogs.init({
    clientToken: CONFIG.datadog.logClientToken || '',
    site: 'datadoghq.com',
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error'],
    sessionSampleRate: 100,
    service: 'onevest-app',
    env: CONFIG.datadog.env,
    version: CONFIG.datadog.ddRumVersion || '',
    beforeSend: (log) => {
      if (/^AbortError\b/i.test(log.error?.stack ?? '')) {
        return false;
      }
      return true;
    },
  });

  datadogRum.init({
    applicationId: CONFIG.datadog.rumAppId || '',
    clientToken: CONFIG.datadog.rumClientToken || '',
    site: 'datadoghq.com',
    service: 'onevest-app',
    version: CONFIG.datadog.ddRumVersion || '',
    env: CONFIG.datadog.env,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: 'mask',
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
  });
}

const InternalApp = () => {
  const { loading, userContext } = useContext(UserContext);
  const theme = useTheme();

  useEffect(() => {
    if (!CONFIG.datadog.env) return;

    datadogRum.setUser({
      id: userContext.id,
      organization: userContext.organization?.translatedName?.en,
      role: userContext.role?.translatedName?.en,
      navigationStyle: userContext.role?.navigationStyle,
    });
  }, [
    userContext.id,
    userContext.organization?.translatedName?.en,
    userContext.role?.translatedName?.en,
    userContext.role?.navigationStyle,
  ]);

  return (
    <Box sx={{ backgroundColor: theme.color.background, height: '100vh' }}>
      {loading ? <BootSpinner /> : <AppRouter />}
    </Box>
  );
};

function App() {
  // IdP-initiated logout runs outside the normal provider tree so auth/user
  // bootstrap never runs on this path (see idpLogout.tsx).
  if (isIdpLogoutRoute()) {
    return <IdpLogout />;
  }

  return (
    <HelmetProvider>
      <FlagsmithProvider
        options={{
          environmentID: CONFIG.services.flagsmithEnvironmentId,
        }}
        flagsmith={flagsmith}
      >
        <ThemeProvider>
          <LocalizedDatePickerProvider>
            <OvApolloProvider>
              <AuthProvider>
                <Auth0Provider>
                  <UserContextProvider>
                    <AnalyticsProvider>
                      <InactivityTimeoutProvider>
                        <InternalApp />
                      </InactivityTimeoutProvider>
                    </AnalyticsProvider>
                  </UserContextProvider>
                </Auth0Provider>
              </AuthProvider>
            </OvApolloProvider>
          </LocalizedDatePickerProvider>
        </ThemeProvider>
      </FlagsmithProvider>
    </HelmetProvider>
  );
}

export default App;
